/**
 * Modal 640 px "Aplicar patrón" (008 · T087 · US5; contracts/ui.md §1.4, spec US5 escenarios 3–5 y
 * 7; contracts/admin-api.md A13).
 *
 * Trabajadores (Combobox que va sumando a una lista, ≤ 200), sede (defecto: la principal de cada
 * uno), "Desde" y "Hasta (opcional)", desfase por trabajador en un ciclo libre ("Empieza en el día N
 * del ciclo"; un patrón semanal va alineado a la semana y no lo lleva) y "Reemplazar también los
 * ajustes manuales" (desmarcado).
 *
 * Flujo en dos pasos, sin optimistic UI:
 * 1. "Revisar" pide el resumen con `?dry_run=true` **conservando** los ajustes manuales: así se sabe
 *    cuántos días manuales hay en el rango ("6 ajustes manuales se conservan"). Si se marcó
 *    reemplazar, se pide además el `dry_run` con `replace_manual: true` para los días que de verdad
 *    se generarían.
 * 2. "Aplicar" solo se habilita si el resumen corresponde a lo que está en pantalla. Si reemplazaría
 *    ajustes manuales, pide una confirmación explícita antes de enviar (FR: nunca pisar un ajuste
 *    manual sin confirmarlo).
 *
 * Los días de un período cerrado nunca se tocan: el resumen los informa ("0 días cerrados").
 */
import { AlertTriangle, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  ApplyPatternBodySchema,
  PATTERN_APPLY_MAX_WORKERS,
  type ApplyPatternBody,
  type ApplyPatternResponse,
  type SchedulePattern,
  type Site,
} from '@fermin/contracts';

import { Button } from '../../../components/ui/button';
import { Combobox } from '../../../components/ui/combobox';
import { Dialog, DialogContent } from '../../../components/ui/dialog';
import { Field } from '../../../components/ui/field';
import { Input } from '../../../components/ui/input';
import { Select, type SelectOption } from '../../../components/ui/select';
import { useToast } from '../../../components/ui/toast';
import { t } from '../../../i18n';
import { readOnlyProps, useReadOnly } from '../../shell/use-read-only';
import { isStatusConflict, scheduleErrorMessage, useApplyPattern, useWorkerOptions } from '../api';
import { formatLocalDay } from '../format';

import type { ApiError } from '../../../api/client';

/** Centinela de "Sede principal de cada trabajador" (`site_id` omitido). */
export const PRIMARY_SITE = 'PRIMARY';

export interface ApplyWorker {
  worker_id: string;
  name: string;
  /** "Empieza en el día N del ciclo" (1-based, texto del input). */
  startAt: string;
}

export interface ApplyValues {
  workers: ApplyWorker[];
  site: string;
  start_day: string;
  end_day: string;
  replace_manual: boolean;
}

type ApplySummary = ApplyPatternResponse['summary'];

interface Review {
  key: string;
  keep: ApplySummary;
  replace: ApplySummary | null;
}

interface ApplyErrors {
  workers?: string;
  start_day?: string;
  end_day?: string;
  site_id?: string;
  byWorker: Record<number, string>;
}

const NO_ERRORS: ApplyErrors = { byWorker: {} };

/** Cuerpo (sin validar) desde el formulario. El desfase es `N − 1` en un ciclo libre y 0 en uno semanal. */
export function applyBodyFrom(
  values: ApplyValues,
  pattern: Pick<SchedulePattern, 'id' | 'kind'>,
): Record<string, unknown> {
  return {
    pattern_id: pattern.id,
    start_day: values.start_day,
    ...(values.end_day ? { end_day: values.end_day } : {}),
    ...(values.site !== PRIMARY_SITE ? { site_id: values.site } : {}),
    workers: values.workers.map((worker) => ({
      worker_id: worker.worker_id,
      cycle_offset: pattern.kind === 'CYCLE' ? Number(worker.startAt) - 1 : 0,
    })),
    replace_manual: values.replace_manual,
  };
}

/** Texto del resumen del `dry_run`. */
export function applySummaryText(review: Pick<Review, 'keep' | 'replace'>): string {
  if (review.replace) {
    return t('schedules.patterns.apply.summaryReplace', {
      generated: review.replace.days_generated,
      manual: review.keep.days_skipped_manual,
      closed: review.replace.days_skipped_closed,
    });
  }
  return t('schedules.patterns.apply.summaryKeep', {
    generated: review.keep.days_generated,
    manual: review.keep.days_skipped_manual,
    closed: review.keep.days_skipped_closed,
  });
}

/** Índice de trabajador de un path de error (`workers.2.cycle_offset`). */
export function workerIndexFromPath(path: unknown): number | null {
  if (Array.isArray(path)) {
    return path[0] === 'workers' && typeof path[1] === 'number' ? path[1] : null;
  }
  if (typeof path !== 'string') {
    return null;
  }
  const match = /^workers(?:\.|\[)(\d+)/.exec(path);
  return match ? Number(match[1]) : null;
}

function issuesToErrors(
  issues: readonly { path?: unknown; message?: unknown }[],
  fallback: string,
): ApplyErrors | null {
  const errors: ApplyErrors = { byWorker: {} };
  let any = false;
  for (const issue of issues) {
    const message = typeof issue.message === 'string' ? issue.message : fallback;
    const index = workerIndexFromPath(issue.path);
    if (index !== null) {
      errors.byWorker[index] ??= message;
      any = true;
      continue;
    }
    const raw = Array.isArray(issue.path) ? issue.path[0] : issue.path;
    const field = typeof raw === 'string' ? raw.split('.')[0] : undefined;
    if (
      field === 'workers' ||
      field === 'start_day' ||
      field === 'end_day' ||
      field === 'site_id'
    ) {
      errors[field] ??= message;
      any = true;
    }
  }
  return any ? errors : null;
}

/** Errores de la API con campo (issues de validación); `null` si no hay dónde ponerlos. */
export function applyApiErrors(error: ApiError): ApplyErrors | null {
  const issues = error.details?.['issues'];
  if (!error.is('VALIDATION_ERROR') || !Array.isArray(issues)) {
    return null;
  }
  return issuesToErrors(issues as { path?: unknown; message?: unknown }[], error.message);
}

function initialValues(today: string): ApplyValues {
  return { workers: [], site: PRIMARY_SITE, start_day: today, end_day: '', replace_manual: false };
}

export interface ApplyPatternDialogProps {
  pattern: SchedulePattern | null;
  onClose: () => void;
  sites: readonly Site[];
  /** Hoy en la zona de la empresa: "Desde" por defecto. */
  today: string;
}

/** El contenido se monta por patrón: cada apertura empieza sin trabajadores ni resumen. */
export function ApplyPatternDialog({ pattern, onClose, sites, today }: ApplyPatternDialogProps) {
  return (
    <Dialog open={pattern !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      {pattern ? (
        <ApplyPatternContent
          key={pattern.id}
          pattern={pattern}
          onClose={onClose}
          sites={sites}
          today={today}
        />
      ) : null}
    </Dialog>
  );
}

function ApplyPatternContent({
  pattern,
  onClose,
  sites,
  today,
}: Omit<ApplyPatternDialogProps, 'pattern'> & { pattern: SchedulePattern }) {
  const { toast } = useToast();
  const { readOnly } = useReadOnly();
  const dryRun = useApplyPattern();
  const commit = useApplyPattern();
  const [values, setValues] = useState<ApplyValues>(() => initialValues(today));
  const [errors, setErrors] = useState<ApplyErrors>(NO_ERRORS);
  const [review, setReview] = useState<Review | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [reviewing, setReviewing] = useState(false);

  const workers = useWorkerOptions(
    values.site !== PRIMARY_SITE
      ? { site_id: values.site, status: 'ACTIVE' }
      : { status: 'ACTIVE' },
  );
  const selectedIds = useMemo(
    () => new Set(values.workers.map((worker) => worker.worker_id)),
    [values.workers],
  );
  const availableWorkers = useMemo(
    () => workers.options.filter((option) => !selectedIds.has(option.value)),
    [workers.options, selectedIds],
  );
  const siteOptions: SelectOption[] = useMemo(
    () => [
      { value: PRIMARY_SITE, label: t('schedules.patterns.apply.primarySite') },
      ...sites.map((site) => ({ value: site.id, label: site.name })),
    ],
    [sites],
  );

  const body = applyBodyFrom(values, pattern);
  const currentKey = JSON.stringify(body);
  const reviewed = review !== null && review.key === currentKey;
  const manualToReplace = reviewed && values.replace_manual ? review.keep.days_skipped_manual : 0;
  const full = values.workers.length >= PATTERN_APPLY_MAX_WORKERS;

  function change(patch: Partial<ApplyValues>) {
    setValues((current) => ({ ...current, ...patch }));
    setConfirming(false);
  }

  function addWorker(workerId: string) {
    const option = workers.options.find((entry) => entry.value === workerId);
    if (!option || selectedIds.has(workerId) || full) {
      return;
    }
    change({
      workers: [...values.workers, { worker_id: workerId, name: option.label, startAt: '1' }],
    });
  }

  function updateWorker(index: number, patch: Partial<ApplyWorker>) {
    change({
      workers: values.workers.map((worker, i) => (i === index ? { ...worker, ...patch } : worker)),
    });
  }

  function removeWorker(index: number) {
    change({ workers: values.workers.filter((_, i) => i !== index) });
  }

  /** Valida en el cliente: contrato + desfase dentro del ciclo. */
  function validate(): ApplyPatternBody | null {
    const next: ApplyErrors = { byWorker: {} };
    let invalid = false;
    if (pattern.kind === 'CYCLE') {
      values.workers.forEach((worker, index) => {
        const n = Number(worker.startAt);
        if (!Number.isInteger(n) || n < 1 || n > pattern.cycle_days) {
          next.byWorker[index] = t('schedules.patterns.apply.errors.startAt', {
            max: pattern.cycle_days,
          });
          invalid = true;
        }
      });
    }
    if (values.workers.length === 0) {
      next.workers = t('schedules.patterns.apply.errors.workers');
      invalid = true;
    }
    const parsed = ApplyPatternBodySchema.safeParse(body);
    if (!parsed.success) {
      const mapped = issuesToErrors(
        parsed.error.issues.map((issue) => ({
          path: issue.path.join('.'),
          message:
            issue.code === 'custom'
              ? issue.message
              : t(`schedules.patterns.apply.errors.${String(issue.path[0])}`),
        })),
        t('common.errors.VALIDATION_ERROR'),
      );
      if (mapped) {
        Object.assign(next.byWorker, mapped.byWorker);
        next.workers ??= mapped.workers;
        next.start_day ??= mapped.start_day;
        next.end_day ??= mapped.end_day;
        next.site_id ??= mapped.site_id;
      }
      invalid = true;
    }
    setErrors(next);
    return invalid || !parsed.success ? null : parsed.data;
  }

  function onApiError(error: ApiError) {
    const mapped = applyApiErrors(error);
    if (mapped) {
      setErrors(mapped);
      return;
    }
    toast({
      title: isStatusConflict(error)
        ? t('schedules.patterns.errors.archived')
        : scheduleErrorMessage(error),
      variant: 'error',
    });
  }

  async function runReview() {
    const parsed = validate();
    if (!parsed) {
      return;
    }
    setReviewing(true);
    try {
      const keep = await dryRun.mutateAsync({
        body: { ...parsed, replace_manual: false },
        dryRun: true,
      });
      const replace = parsed.replace_manual
        ? await dryRun.mutateAsync({ body: parsed, dryRun: true })
        : null;
      setReview({ key: currentKey, keep: keep.summary, replace: replace?.summary ?? null });
      setConfirming(false);
    } catch (error) {
      onApiError(error as ApiError);
    } finally {
      setReviewing(false);
    }
  }

  function runApply() {
    const parsed = validate();
    if (!parsed) {
      return;
    }
    if (manualToReplace > 0 && !confirming) {
      setConfirming(true);
      return;
    }
    commit.mutate(
      { body: parsed, dryRun: false },
      {
        onSuccess: (result) => {
          toast({
            title: t('schedules.patterns.toast.applied', {
              name: pattern.name,
              workers: result.summary.workers,
              days: result.summary.days_generated,
            }),
            variant: 'success',
          });
          onClose();
        },
        onError: onApiError,
      },
    );
  }

  const busy = reviewing || commit.isPending;

  return (
    <DialogContent
      size="detail"
      title={t('schedules.patterns.apply.title', { name: pattern.name })}
      description={t('schedules.patterns.apply.description')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.buttons.cancel')}
          </Button>
          <Button
            variant="secondary"
            data-testid="pattern-apply-review"
            loading={reviewing}
            loadingText={t('common.states.processing')}
            disabled={busy}
            onClick={() => void runReview()}
          >
            {t('schedules.patterns.apply.review')}
          </Button>
          <Button
            data-testid="pattern-apply-submit"
            variant={confirming ? 'destructive' : 'primary'}
            loading={commit.isPending}
            loadingText={t('common.states.saving')}
            disabled={!reviewed || busy}
            onClick={runApply}
            {...readOnlyProps(readOnly)}
          >
            {confirming
              ? t('schedules.patterns.apply.confirmReplace', { count: manualToReplace })
              : t('schedules.patterns.apply.submit')}
          </Button>
        </>
      }
    >
      <div
        data-testid="pattern-apply-dialog"
        className="flex max-h-[65vh] flex-col gap-fm-4 overflow-y-auto pr-fm-1"
      >
        <Field
          label={t('schedules.patterns.apply.workers')}
          required
          help={t('schedules.patterns.apply.workersHelp', {
            count: values.workers.length,
            max: PATTERN_APPLY_MAX_WORKERS,
          })}
          error={errors.workers}
        >
          {(control) => (
            <Combobox
              id={control.id}
              aria-describedby={control['aria-describedby']}
              aria-required={control['aria-required']}
              invalid={errors.workers !== undefined}
              disabled={full}
              value=""
              onValueChange={addWorker}
              options={availableWorkers}
              placeholder={t('schedules.patterns.apply.addWorker')}
              searchPlaceholder={t('absences.form.workerSearch')}
            />
          )}
        </Field>

        {values.workers.length > 0 ? (
          <ul className="flex flex-col gap-fm-2" data-testid="pattern-apply-workers">
            {values.workers.map((worker, index) => (
              <li
                key={worker.worker_id}
                data-testid="pattern-apply-worker"
                className="grid grid-cols-[1fr_auto_auto] items-start gap-fm-2 rounded-fm-md border border-fm-border p-fm-2"
              >
                <span className="flex min-h-fm-control-sm items-center text-fm-sm font-medium">
                  {worker.name}
                </span>
                {pattern.kind === 'CYCLE' ? (
                  <Field
                    label={t('schedules.patterns.apply.startAt', { max: pattern.cycle_days })}
                    className="w-[200px]"
                    error={errors.byWorker[index]}
                  >
                    {(control) => (
                      <Input
                        {...control}
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={pattern.cycle_days}
                        step={1}
                        value={worker.startAt}
                        onChange={(event) => updateWorker(index, { startAt: event.target.value })}
                      />
                    )}
                  </Field>
                ) : errors.byWorker[index] ? (
                  <p className="text-fm-xs text-fm-danger-text" role="alert">
                    {errors.byWorker[index]}
                  </p>
                ) : (
                  <span aria-hidden="true" />
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<X size={16} />}
                  aria-label={t('schedules.patterns.apply.removeWorker', { name: worker.name })}
                  onClick={() => removeWorker(index)}
                />
              </li>
            ))}
          </ul>
        ) : null}

        <Field label={t('schedules.patterns.apply.site')} error={errors.site_id}>
          {(control) => (
            <Select
              id={control.id}
              aria-describedby={control['aria-describedby']}
              value={values.site}
              onValueChange={(site) => change({ site })}
              options={siteOptions}
            />
          )}
        </Field>

        <div className="grid grid-cols-2 gap-fm-3">
          <Field label={t('schedules.patterns.apply.startDay')} required error={errors.start_day}>
            {(control) => (
              <Input
                {...control}
                type="date"
                value={values.start_day}
                onChange={(event) => change({ start_day: event.target.value })}
              />
            )}
          </Field>
          <Field label={t('schedules.patterns.apply.endDay')} error={errors.end_day}>
            {(control) => (
              <Input
                {...control}
                type="date"
                min={values.start_day || undefined}
                value={values.end_day}
                onChange={(event) => change({ end_day: event.target.value })}
              />
            )}
          </Field>
        </div>

        <label className="flex min-h-fm-touch items-center gap-fm-2 text-fm-sm text-fm-text">
          <input
            type="checkbox"
            data-testid="pattern-apply-replace-manual"
            className="size-4 accent-[var(--fm-primary)]"
            checked={values.replace_manual}
            onChange={(event) => change({ replace_manual: event.target.checked })}
          />
          {t('schedules.patterns.apply.replaceManual')}
        </label>

        <div
          data-testid="pattern-apply-summary"
          data-state={reviewed ? 'ready' : review ? 'stale' : 'empty'}
          aria-live="polite"
          className="rounded-fm-md bg-fm-bg-sunken p-fm-3 text-fm-sm"
        >
          {reviewed ? (
            <>
              <p className="font-semibold text-fm-text">{applySummaryText(review)}</p>
              <p className="mt-fm-1 text-fm-xs text-fm-text-secondary">
                {t('schedules.patterns.apply.generatedUntil', {
                  day: formatLocalDay((review.replace ?? review.keep).generated_until),
                })}
              </p>
            </>
          ) : (
            <p className="text-fm-text-secondary">
              {review
                ? t('schedules.patterns.apply.summaryStale')
                : t('schedules.patterns.apply.summaryEmpty')}
            </p>
          )}
        </div>

        {confirming ? (
          <div
            role="alert"
            data-testid="pattern-apply-confirm"
            className="flex gap-fm-2 rounded-fm-md border border-fm-warning bg-fm-warning-soft p-fm-3 text-fm-sm text-fm-warning-text"
          >
            <AlertTriangle size={16} aria-hidden="true" className="mt-px shrink-0" />
            <p>{t('schedules.patterns.apply.confirmText', { count: manualToReplace })}</p>
          </div>
        ) : null}
      </div>
    </DialogContent>
  );
}
